/**
 * Billing tasks API (GDN-2644) — the human billing work queue.
 *
 * Existing friendly endpoints retain their callers; all payload types now
 * come from the generated billing-v2 contract, including exact issue linkage.
 */
import { baseApi } from '@store/baseApi';
import type {
  BillingTaskItem,
  BillingTaskListResponse,
  BillingTaskResponse,
  CreateBillingTaskRequest,
  GetV2ProviderBillingV2TasksApiArg,
  UpdateBillingTaskStatusRequest,
} from '@store/generated/billing-v2';

export type {
  BillingTaskCategory,
  BillingTaskItem,
  BillingTaskListResponse,
  BillingTaskResponse,
  BillingTaskStatus,
  BillingTaskSubjectType,
} from '@store/generated/billing-v2';
export type PrincipalKind = BillingTaskItem['created_by_principal_kind'];
export type BillingTaskSegment = NonNullable<BillingTaskListResponse['segments']>[number];
export type BillingTasksQueryArgs = GetV2ProviderBillingV2TasksApiArg;
export type CreateBillingTaskArgs = CreateBillingTaskRequest;
/** Existing camel-case UI adapter; payload fields come from the generated request. */
export type UpdateBillingTaskStatusArgs = {
  billingTaskId: string;
  status: UpdateBillingTaskStatusRequest['status'];
  resolutionNote?: UpdateBillingTaskStatusRequest['resolution_note'];
};

const TASKS_TAG = 'BillingV2Tasks' as const;

function compactParams<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
  ) as Partial<T>;
}

const tasksApi = baseApi.enhanceEndpoints({ addTagTypes: [TASKS_TAG] }).injectEndpoints({
  endpoints: build => ({
    getBillingTasks: build.query<BillingTaskListResponse, BillingTasksQueryArgs | undefined>({
      query: args => ({
        url: '/v2/provider/billing/v2/tasks',
        params: compactParams({
          status: args?.status,
          category: args?.category,
          subject_type: args?.subjectType,
          subject_id: args?.subjectId,
          page: args?.page,
          per_page: args?.perPage,
        }),
      }),
      providesTags: [TASKS_TAG],
    }),
    createBillingTask: build.mutation<BillingTaskResponse, CreateBillingTaskArgs>({
      query: body => ({
        url: '/v2/provider/billing/v2/tasks',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TASKS_TAG],
    }),
    updateBillingTaskStatus: build.mutation<BillingTaskResponse, UpdateBillingTaskStatusArgs>({
      query: ({ billingTaskId, status, resolutionNote }) => ({
        url: `/v2/provider/billing/v2/tasks/${billingTaskId}/status`,
        method: 'PATCH',
        body: compactParams({ status, resolution_note: resolutionNote }),
      }),
      invalidatesTags: [TASKS_TAG],
    }),
  }),
});

export const {
  useGetBillingTasksQuery: useBillingTasksQuery,
  useCreateBillingTaskMutation,
  useUpdateBillingTaskStatusMutation,
} = tasksApi;

export { tasksApi };
